import React from 'react';
import moment from 'moment';
import { Box } from '@mui/material';
import StoreButton from '../../../../Components/Forms/Store/StoreButton';
import StoreTable from '../../../../Components/Store/StoreTable';
import LocalisedDisplayDate from '../../../../Components/Locale/LocalisedDisplayDate';
import { useTranslation } from 'react-i18next';

// dates + action buttons don't wrap from sm up, so the table's auto layout takes the
// space from the product title instead (on xs the table stacks into blocks anyway)
const noWrapFromSm = { sm: 'nowrap' };

const NoWrap = ({ children }) => (
  <Box component='span' sx={{ whiteSpace: noWrapFromSm }}>
    {children}
  </Box>
);

const NoWrapDate = ({ date }) => (
  <NoWrap>
    <LocalisedDisplayDate date={date} />
  </NoWrap>
);

const Actions = (props) => {
  const {
    userSubscriptionPlan,
    onCancelSubscriptionClick,
    onChangeSubscriptionClick,
    onRestoreSubscriptionClick
  } = props;

  const handleCancelClick = () => {
    onCancelSubscriptionClick(userSubscriptionPlan.id);
  };

  const handleChangeClick = () => {
    onChangeSubscriptionClick(userSubscriptionPlan.id);
  };

  const handleRestoreClick = () => {
    onRestoreSubscriptionClick(userSubscriptionPlan.id);
  };

  return (
    <Box sx={{ display: { xs: 'block', sm: 'flex' }, whiteSpace: noWrapFromSm }}>
      {userSubscriptionPlan.endDateTime
        ? (<StoreButton onClick={handleRestoreClick} label='restore' dataTestId='subscription-restore' />)
        : (
          <>
            <div>
              <StoreButton onClick={handleCancelClick} label='cancel' dataTestId='subscription-cancel' />
            </div>
            {
              userSubscriptionPlan.subscriptionPlan.type === 'Monthly' && (
                <Box sx={{
                  marginLeft: { sm: 1 },
                  marginTop: { xs: 1, sm: 0 }
                }}
                >
                  <StoreButton
                    onClick={handleChangeClick}
                    label='change-to-yearly'
                    dataTestId='change-to-yearly'
                  />
                </Box>
              )
            }
          </>
          )}
    </Box>
  );
};

export default function SubscriptionTable ({
  expired,
  userSubscriptionPlans,
  onCancelSubscriptionClick,
  onChangeSubscriptionClick,
  onRestoreSubscriptionClick
}) {
  const { t } = useTranslation();

  const aSubscriptionIsCancelling = userSubscriptionPlans.some(
    usp => usp.endDateTime && moment(usp.endDateTime) > Date.now()
  );

  // only active, non-cancelling subscriptions renew
  const aSubscriptionIsRenewing = !expired && userSubscriptionPlans.some(usp => !usp.endDateTime);

  const rows = userSubscriptionPlans.map((usp, index) => {
    const row = [
      usp.subscriptionPlan.subscription.title,
      usp.subscriptionPlan.type,
      <NoWrapDate key={index} date={usp.startDateTime} />,
      !expired && (
        <Actions
          key={index}
          userSubscriptionPlan={usp}
          onCancelSubscriptionClick={onCancelSubscriptionClick}
          onChangeSubscriptionClick={onChangeSubscriptionClick}
          onRestoreSubscriptionClick={onRestoreSubscriptionClick}
        />
      )
    ];

    if (aSubscriptionIsRenewing) {
      row.splice(3, 0, usp.renewalDateTime
        ? (<NoWrapDate date={usp.renewalDateTime} />)
        : null);
    }

    if (aSubscriptionIsCancelling || expired) {
      row.splice(3, 0, usp.endDateTime
        ? (<NoWrapDate date={usp.endDateTime} />)
        : null);
    }

    return row;
  });

  const tableData = {
    columns: [
      t('product'),
      t('plan'),
      t('start-date'),
      t('actions')
    ],
    rows
  };

  if (aSubscriptionIsRenewing) {
    tableData.columns.splice(3, 0, t('renews-on'));
  }

  if (aSubscriptionIsCancelling) {
    tableData.columns.splice(3, 0, t('cancelling-on'));
  }

  if (expired) {
    tableData.columns.splice(3, 0, t('end-date'));
    tableData.columns.splice(4);
  }

  tableData.columns = tableData.columns.map((column, index) => (
    <NoWrap key={index}>{column}</NoWrap>
  ));

  return (
    <StoreTable tableData={tableData} />
  );
}
